import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { InputPrice } from '@/components/element/Form/InputPrice';
import { SelectMulti } from '@/components/element/Form/SelectMulti';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import { SelectSingle } from '@/components/element/Form/SelectSingle';
import { ScrollTop } from '@/components/element/ScrollTop';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useBusinessPartners } from '@/hooks/api/0430_business_partners';
import { usePaymentMethods } from '@/hooks/api/0510_payment_methods';
import { usePaymentContexts } from '@/hooks/api/0511_payment_contexts';
import { usePurchaseOrdersInvalidate } from '@/hooks/api/0520_purchase_orders';
import { useUpdatePurchaseOrder } from '@/hooks/api/0540_update_purchase_order';
import { usePurchaseOrderTargetUsers } from '@/hooks/api/0541_purchase_order_target_users';
import { usePaymentTasks } from '@/hooks/api/0960_payment_tasks';
import { useExpenseAccountItems } from '@/hooks/api/2340_expense_account_items';
import { BusinessPartner, ExpenseAccountItem, PaymentContext, PaymentMethod, User } from '@/hooks/api/models';
import { ProjectLogic, TeamMemberLogic } from '@/hooks/api/models/logics';
import { toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentPurchaseOrder } from '@/providers/PurchaseOrderProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { scrollTop } from '@/utils';
import { Japanese } from 'flatpickr/dist/l10n/ja';
import { useEffect, useMemo, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MultiValue, SingleValue } from 'react-select';
import { ModalConfirm } from '@/components/element/modal/ModalConfirm';

type FormInputs = {
  identifier: string;
  team_id: number;
  id: number;
  name: string;
  description: string;
  user_id: number;
  business_partner_id: number;
  person_in_charge?: string;
  payment_context_id: number;
  spot_amount?: number;
  spot_billing_date?: string;
  monthly_amount?: number;
  monthly_billing_start_date?: string;
  monthly_billing_end_date?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  delivery_date?: string;
  payment_method_id: number;
  transfer_information?: string;
  fixed_asset_target: boolean;
  task_procedure_manual?: string;
  expense_account_item_ids: number[];
};

type Props = {
  targetUsers: User[];
  businessPartners: BusinessPartner[];
  paymentContexts: PaymentContext[];
  paymentMethods: PaymentMethod[];
  paymentTaskExists: boolean;
  expenseAccountItems: ExpenseAccountItem[];
};

export const PurchaseOrderEdit = () => {
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _po_ } = useCurrentPurchaseOrder();

  const { data: targetUsers } = usePurchaseOrderTargetUsers({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });
  const { data: businessPartnersData } = useBusinessPartners({
    identifier: _w_!.identifier,
    display_type: 'purchase',
    page: 1,
    per: 1000,
  });
  const { data: paymentContexts } = usePaymentContexts();
  const { data: paymentMethods } = usePaymentMethods();
  const { data: expenseAccountItems } = useExpenseAccountItems();
  const { data: paymentTaskRes } = usePaymentTasks({
    identifier: _w_!.identifier,
    team_ids: [_t_!.id],
    purchase_order_id: _po_!.id,
  });

  const [businessPartners, setBusinessPartners] = useState<BusinessPartner[] | null>(null);
  const [paymentTaskExists, setPaymentTaskExists] = useState<boolean | null>(null);

  useEffect(() => {
    if (businessPartnersData) setBusinessPartners(() => businessPartnersData.business_partners);
  }, [businessPartnersData]);

  useEffect(() => {
    if (paymentTaskRes) setPaymentTaskExists(paymentTaskRes.pager.total_pages > 0);
  }, [paymentTaskRes]);

  if (
    targetUsers == null ||
    businessPartners == null ||
    paymentContexts == null ||
    paymentMethods == null ||
    paymentTaskExists == null ||
    expenseAccountItems == null
  )
    return <></>;

  return (
    <_PurchaseOrderEdit
      targetUsers={targetUsers}
      businessPartners={businessPartners}
      paymentContexts={paymentContexts}
      paymentMethods={paymentMethods}
      paymentTaskExists={paymentTaskExists}
      expenseAccountItems={expenseAccountItems}
    />
  );
};

export const _PurchaseOrderEdit = ({
  targetUsers,
  businessPartners,
  paymentContexts,
  paymentMethods,
  paymentTaskExists,
  expenseAccountItems,
}: Props) => {
  const { setTitle, setBreadcrumbs } = useNavState();
  const { _w_, _t_, me, meTeamMember, isTeamAdmin, isApprover, isFinancialOfficer, isBackoffice, isMember } =
    useCurrentTeamMember();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  useEffect(() => setTitle(`プロジェクト（費用）編集`), []);
  useEffect(
    () =>
      setBreadcrumbs([
        {
          label: `プロジェクト（費用） - ${_t_!.name}`,
          path: `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders`,
        },
        {
          label: `プロジェクト（費用）詳細`,
          path: `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${_po_!.id}`,
        },
      ]),
    []
  );

  const location = useLocation();
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { mutate, data, isPending, error } = useUpdatePurchaseOrder();
  const [paymentCtx, setPaymentCtx] = useState<PaymentContext>();
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const { invalidate } = usePurchaseOrdersInvalidate(_w_!.identifier, _t_!.id);
  const [businessPartnerOptions, setBusinessPartnerOptions] = useState<SelectOptionType[]>(() =>
    businessPartners.map((d): SelectOptionType => ({ value: d.id, label: d.display_name }))
  );
  const [expenseAccountItemOptions, setExpenseAccountItemOptions] = useState<SelectOptionType[]>(() =>
    expenseAccountItems.map((d): SelectOptionType => ({ value: d.id, label: d.label }))
  );
  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    trigger,
    resetField,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      id: _po_!.id,
    },
  });
  const [paymentContextId, monthlyAmount, monthlyBillingStartDate, monthlyBillingEndDate] = watch([
    'payment_context_id',
    'monthly_amount',
    'monthly_billing_start_date',
    'monthly_billing_end_date',
  ]);

  useEffect(() => {
    setValue('business_partner_id', _po_!.business_partner.id);
    setValue('payment_context_id', _po_!.payment_context.id);
    setValue('payment_method_id', _po_!.payment_method.id);
    setValue('name', _po_!.name);
    setValue('description', _po_!.description || '');
    setValue('user_id', _po_!.user.id);
    setValue('person_in_charge', _po_!.person_in_charge);
    if (_po_!.payment_context.name == 'spot' || _po_!.payment_context.name == 'spot_and_monthly') {
      setValue('spot_amount', _po_!.spot_amount);
      setValue('spot_billing_date', _po_!.spot_billing_date?.replace(/-/g, '/'));
    }
    if (_po_!.payment_context.name == 'monthly' || _po_!.payment_context.name == 'spot_and_monthly') {
      setValue('monthly_amount', _po_!.monthly_amount);
      setValue('monthly_billing_start_date', _po_!.monthly_billing_start_date?.replace(/-/g, '/'));
      setValue('monthly_billing_end_date', _po_!.monthly_billing_end_date?.replace(/-/g, '/'));
    }
    setValue('contract_start_date', _po_!.contract_start_date?.replace(/-/g, '/'));
    setValue('contract_end_date', _po_!.contract_end_date?.replace(/-/g, '/'));
    setValue('delivery_date', _po_!.delivery_date?.replace(/-/g, '/'));
    setValue('transfer_information', _po_!.transfer_information);
    setValue('fixed_asset_target', _po_!.fixed_asset_target);
    setValue('task_procedure_manual', _po_!.task_procedure_manual);
    setValue(
      'expense_account_item_ids',
      _po_!.expense_account_items.map((a) => a.id)
    );
  }, []);

  const isEditable = useMemo(() => {
    if (meTeamMember && TeamMemberLogic.hasApproverRole(meTeamMember)) {
      return true;
    } else {
      return !_po_!.closed;
    }
  }, [meTeamMember, _po_]);

  useEffect(() => {
    if (paymentContextId) {
      setPaymentCtx(paymentContexts?.find((pc) => pc.id == paymentContextId));
    }
  }, [paymentContextId]);

  useEffect(() => {
    if (monthlyAmount && monthlyBillingStartDate && monthlyBillingEndDate) {
      setTotalAmount((_) =>
        ProjectLogic.calcTotalAmount(monthlyAmount, monthlyBillingStartDate, monthlyBillingEndDate)
      );
    } else {
      setTotalAmount((_) => 0);
    }
  }, [monthlyAmount, monthlyBillingStartDate, monthlyBillingEndDate]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      invalidate();
      toastSuccess('プロジェクト（費用）の編集が完了しました');
      if (location?.state?.prevPath) {
        // navigate(location?.state?.prevPath);
        navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${data.id}/payment_schedules`, {
          state: { prevPath: location?.state?.prevPath },
        });
      } else {
        // navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${data.id}`);
        navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${data.id}/payment_schedules`);
      }
    }
  }, [data]);

  useEffect(() => {
    if (error) scrollTop();
  }, [error]);

  const [doSubmit, setDoSubmit] = useState(false);
  const [formInputData, setFormInputData] = useState<FormInputs>();

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setFormInputData((_) => data);

    // Type
    // SpotPrice, PostingDate
    // MonthlyFee, BillingStartDate, BillingEndDate
    // に変更が入った際は確認ダイアログを表示
    let showConfirm = false;
    if (_po_!.payment_context.id !== getValues('payment_context_id')) {
      showConfirm = true;
    } else {
      if (_po_!.payment_context.name == 'spot' || _po_!.payment_context.name == 'spot_and_monthly') {
        if (_po_!.spot_amount !== getValues('spot_amount')) showConfirm = true;
        if (_po_!.spot_billing_date?.replace(/-/g, '/') !== getValues('spot_billing_date')) showConfirm = true;
      }

      if (_po_!.payment_context.name == 'monthly' || _po_!.payment_context.name == 'spot_and_monthly') {
        if (_po_!.monthly_amount !== getValues('monthly_amount')) showConfirm = true;
        if (_po_!.monthly_billing_start_date?.replace(/-/g, '/') !== getValues('monthly_billing_start_date'))
          showConfirm = true;
        if (_po_!.monthly_billing_end_date?.replace(/-/g, '/') !== getValues('monthly_billing_end_date'))
          showConfirm = true;
      }
    }

    if (showConfirm) {
      setDoSubmit(false);
      document.getElementById('edit-confirm-button')?.click();
    } else {
      setDoSubmit(true);
      onEditClick();
    }
  };

  useEffect(() => {
    if (formInputData && doSubmit) {
      setShowLoading(true);
      mutate(formInputData!);
    }
  }, [formInputData, doSubmit]);

  const onEditClick = () => {
    setDoSubmit(true);
  };

  const onBusinessPartnerChange = (sv: SingleValue<SelectOptionType>) => {
    if (sv) {
      setValue('business_partner_id', sv.value);
      trigger('business_partner_id');
    } else {
      resetField('business_partner_id');
    }
  };

  const onAccountTitlessChange = (d: MultiValue<SelectOptionType>) => {
    if (d) {
      setValue(
        'expense_account_item_ids',
        d.map((a) => a.value)
      );
      trigger('expense_account_item_ids');
    } else {
      resetField('expense_account_item_ids');
    }
  };

  return (
    <WorkspaceLayout>
      <ScrollTop />

      <ModalConfirm
        modalId={`editConfirm`}
        okClicked={() => onEditClick()}
        cancelClicked={() => {
          setFormInputData(undefined);
          setDoSubmit(false);
        }}
        message={
          'プロジェクトの支払金額にかかわる箇所の変更を行います。\nそのため、変更済の支払計画がリセットされ再設定されます。\n\nよろしいですか？'
        }
      />
      <button
        data-bs-toggle="modal"
        data-bs-target={`#editConfirm`}
        style={{ display: 'none' }}
        id="edit-confirm-button"
      />

      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">プロジェクト（費用）編集</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0 row">
                  <ApiErrorMessages error={error} />

                  <div className="mb-3">
                    <div className="mb-0 font-weight-bold">プロジェクト基本情報</div>
                  </div>
                  <FormGroup label="プロジェクト名" required={true}>
                    <input
                      type="text"
                      maxLength={50}
                      className={`form-control ${errors.name && 'is-invalid'}`}
                      placeholder={'案件名'}
                      {...register('name', {
                        required: '入力して下さい。',
                      })}
                    />
                    {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                  </FormGroup>

                  <FormGroup label="メモ">
                    <textarea
                      className="form-control"
                      maxLength={2000}
                      rows={5}
                      {...register('description')}
                    ></textarea>
                  </FormGroup>

                  <div className="row">
                    <div className="col-12 col-md-6 pe-0">
                      <FormGroup label="プロジェクト担当者" required={true}>
                        <select className="form-control" required={true} {...register('user_id')}>
                          {targetUsers.map((tu, i) => (
                            <option key={`target-user-${tu.id}`} value={tu.id}>
                              {tu.user_account.name}
                            </option>
                          ))}
                        </select>
                      </FormGroup>
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <FormGroup label="発注先" required={true}>
                      <Controller
                        control={control}
                        name="business_partner_id"
                        rules={{ required: true }}
                        render={({ field: { ref, ...field } }) => (
                          <SelectSingle
                            options={businessPartnerOptions}
                            selectedId={getValues('business_partner_id')}
                            onChange={onBusinessPartnerChange}
                            error={errors.business_partner_id != null}
                            disabled={!isEditable}
                          />
                        )}
                      />
                    </FormGroup>
                  </div>

                  <div className="col-12 col-md-6">
                    <FormGroup label="発注先担当者">
                      <input type="text" className="form-control" {...register('person_in_charge')} />
                    </FormGroup>
                  </div>

                  <div className="my-3">
                    <div className="mb-1 font-weight-bold">プロジェクト詳細情報</div>
                    <p className="text-xs mb-0">契約タスク承認後は承認者のみが金額や期間の変更を行えます</p>
                  </div>

                  <div className="row">
                    <div className="col-12 col-md-6 pe-0">
                      <FormGroup label="支払タイプ" required={true}>
                        <select
                          className="form-control"
                          required={true}
                          {...register('payment_context_id')}
                          disabled={!isEditable}
                        >
                          {paymentContexts.map((pc, i) => (
                            <option key={`pc-${pc.id}`} value={pc.id}>
                              {pc.label}
                            </option>
                          ))}
                        </select>
                      </FormGroup>
                    </div>
                  </div>

                  {paymentCtx && (paymentCtx.name == 'spot' || paymentCtx.name == 'spot_and_monthly') && (
                    <>
                      <div className="col-12 col-md-6">
                        <FormGroup label="一括支払金額（税抜）" required={true}>
                          <div className="input-group">
                            <Controller
                              render={({ field: { onChange, value, ...field } }) => (
                                <InputPrice
                                  onChange={onChange}
                                  value={value}
                                  error={errors.spot_amount}
                                  disabled={!isEditable}
                                />
                              )}
                              control={control}
                              name="spot_amount"
                              rules={{ required: true }}
                            />
                          </div>
                          <div className="text-xs text-muted mt-1">納品時等一括で支払う金額</div>
                        </FormGroup>
                      </div>

                      <div className="col-12 col-md-6">
                        <FormGroup label="請求日" required={true}>
                          <Controller
                            control={control}
                            name="spot_billing_date"
                            rules={{ required: true }}
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr ${errors.spot_billing_date && 'is-invalid'}`}
                                {...field}
                                onChange={(_, dateString) => {
                                  const cur = {
                                    contractEndDate: getValues('contract_end_date') || '',
                                    spotBillingDate: getValues('spot_billing_date') || '',
                                    monthlyBillingEndDate: getValues('monthly_billing_end_date') || '',
                                    deliveryDate: getValues('delivery_date') || '',
                                  };
                                  const dst = { ...cur, spotBillingDate: dateString };
                                  const contractEndDate = ProjectLogic.detectContractEndDate({ cur, dst });
                                  setValue('contract_end_date', contractEndDate);

                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={(e) => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                                disabled={!isEditable}
                              />
                            )}
                          />
                        </FormGroup>
                      </div>
                    </>
                  )}

                  {paymentCtx && (paymentCtx.name == 'monthly' || paymentCtx.name == 'spot_and_monthly') && (
                    <>
                      <div className="col-12 col-md-6">
                        <FormGroup label="月額支払金額（税抜）" required={true}>
                          <div className="input-group">
                            <Controller
                              render={({ field: { onChange, value, ...field } }) => (
                                <InputPrice
                                  onChange={onChange}
                                  value={value}
                                  error={errors.monthly_amount}
                                  disabled={!isEditable}
                                />
                              )}
                              control={control}
                              name="monthly_amount"
                              rules={{ required: true }}
                            />
                          </div>
                          <div className="text-xs text-muted mt-1">&nbsp;</div>
                        </FormGroup>
                      </div>

                      <div className="col-12 col-md-6">
                        <FormGroup label="月額合計金額">
                          <input
                            type="text"
                            className="form-control text-end"
                            value={`¥${totalAmount.toLocaleString()}`}
                            disabled
                          />
                          <div className="text-xs text-muted mt-1">自動計算されます</div>
                        </FormGroup>
                      </div>

                      <div className="col-12 col-md-6">
                        <FormGroup label="支払対象期間の開始日" required={true}>
                          <Controller
                            control={control}
                            name="monthly_billing_start_date"
                            rules={{ required: true }}
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr ${
                                  errors.monthly_billing_start_date && 'is-invalid'
                                }`}
                                {...field}
                                onChange={(_, dateString) => {
                                  // monthly_billing_start_dateと契約開始日が同じなら上書き
                                  const curMonthlyBillingStartDate = getValues('monthly_billing_start_date');
                                  const curContractStartDate = getValues('contract_start_date');
                                  if (
                                    curContractStartDate == null ||
                                    curMonthlyBillingStartDate == curContractStartDate
                                  ) {
                                    setValue('contract_start_date', dateString);
                                  }

                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={() => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                                disabled={!isEditable}
                              />
                            )}
                          />
                          <div className="text-xs text-muted mt-1">月額金額の請求対象となる期間の開始日</div>
                        </FormGroup>
                      </div>

                      <div className="col-12 col-md-6">
                        <FormGroup label="支払対象期間の終了日" required={true}>
                          <Controller
                            control={control}
                            name="monthly_billing_end_date"
                            rules={{ required: true }}
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr ${errors.monthly_billing_end_date && 'is-invalid'}`}
                                {...field}
                                onChange={(_, dateString) => {
                                  const cur = {
                                    contractEndDate: getValues('contract_end_date') || '',
                                    spotBillingDate: getValues('spot_billing_date') || '',
                                    monthlyBillingEndDate: getValues('monthly_billing_end_date') || '',
                                    deliveryDate: getValues('delivery_date') || '',
                                  };
                                  const dst = { ...cur, monthlyBillingEndDate: dateString };
                                  const contractEndDate = ProjectLogic.detectContractEndDate({ cur, dst });
                                  setValue('contract_end_date', contractEndDate);

                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={() => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                                disabled={!isEditable}
                              />
                            )}
                          />
                          <div className="text-xs text-muted mt-1">月額金額の請求対象となる期間の終了日</div>
                        </FormGroup>
                      </div>
                    </>
                  )}

                  <div className="col-12 col-md-6">
                    <FormGroup label="契約開始日">
                      <Controller
                        control={control}
                        name="contract_start_date"
                        render={({ field: { ref, ...field } }) => (
                          <Flatpickr
                            className={`form-control datepickr`}
                            {...field}
                            onChange={(_, dateString) => {
                              field.onChange(dateString);
                              field.onBlur();
                            }}
                            onBlur={() => {}}
                            options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                          />
                        )}
                      />
                      <div className="text-xs text-muted mt-1">契約書・発注書等に記載の契約開始となる日付</div>
                    </FormGroup>
                  </div>

                  <div className="col-12 col-md-6">
                    <FormGroup label="契約終了日">
                      <Controller
                        control={control}
                        name="contract_end_date"
                        render={({ field: { ref, ...field } }) => (
                          <Flatpickr
                            className={`form-control datepickr`}
                            {...field}
                            onChange={(_, dateString) => {
                              field.onChange(dateString);
                              field.onBlur();
                            }}
                            onBlur={() => {}}
                            options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                          />
                        )}
                      />
                      <div className="text-xs text-muted mt-1">契約書・発注書等に記載の契約終了となる日付</div>
                    </FormGroup>
                  </div>

                  <div className="col-12 col-md-6">
                    <FormGroup label="納品日">
                      <Controller
                        control={control}
                        name="delivery_date"
                        render={({ field: { ref, ...field } }) => (
                          <Flatpickr
                            className={`form-control datepickr`}
                            {...field}
                            onChange={(_, dateString) => {
                              const cur = {
                                contractEndDate: getValues('contract_end_date') || '',
                                spotBillingDate: getValues('spot_billing_date') || '',
                                monthlyBillingEndDate: getValues('monthly_billing_end_date') || '',
                                deliveryDate: getValues('delivery_date') || '',
                              };
                              const dst = { ...cur, deliveryDate: dateString };
                              const contractEndDate = ProjectLogic.detectContractEndDate({ cur, dst });
                              setValue('contract_end_date', contractEndDate);

                              field.onChange(dateString);
                              field.onBlur();
                            }}
                            onBlur={() => {}}
                            options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                          />
                        )}
                      />
                      <div className="text-xs text-muted mt-1">契約書・発注書等に記載の納品の日付</div>
                    </FormGroup>
                  </div>

                  <div className="row pe-0">
                    <div className="col-12 col-md-6 pe-0">
                      <FormGroup label="支払方法" required={true}>
                        <select className="form-control" required={true} {...register('payment_method_id')}>
                          {paymentMethods.map((pm, i) => (
                            <option key={`pm-${pm.id}`} value={pm.id}>
                              {pm.label}
                            </option>
                          ))}
                        </select>
                      </FormGroup>
                    </div>

                    <div className="col-12 col-md-6 ps-4 pe-0">
                      <FormGroup label="勘定項目" required={true}>
                        <Controller
                          control={control}
                          name="expense_account_item_ids"
                          rules={{ required: true }}
                          render={({ field: { ref, ...field } }) => (
                            <SelectMulti
                              options={expenseAccountItemOptions}
                              selectedIds={getValues('expense_account_item_ids')}
                              classes={'w-100'}
                              onChange={onAccountTitlessChange}
                              error={errors.expense_account_item_ids != null}
                            />
                          )}
                        />
                        <div className="text-xs text-muted mt-1">支出の詳細を管理するための項目を設定して下さい</div>
                      </FormGroup>
                    </div>
                  </div>

                  <div className="form-group">
                    <FormGroup label="振込先情報">
                      <textarea className="form-control" {...register('transfer_information')}></textarea>
                      <div className="text-xs text-muted mt-1">支払タスクに連携される振込先に関する情報</div>
                    </FormGroup>
                  </div>

                  <div className="form-group">
                    <FormGroup label="支払タスク処理マニュアル">
                      <textarea className="form-control" {...register('task_procedure_manual')}></textarea>
                      <div className="text-xs text-muted mt-1">支払タスクに連携される事務処理手順</div>
                    </FormGroup>
                  </div>

                  <FormGroup label="固定資産">
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="fixed-asset"
                        className="form-check-input"
                        {...register('fixed_asset_target')}
                      />
                      <label className="form-check-label" htmlFor="fixed-asset">
                        固定資産が作成されるプロジェクトの場合はONにします。
                      </label>
                    </div>
                  </FormGroup>
                </div>

                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                    戻る
                  </Link>
                  <button className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>変更を確定する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
